import dayjs from "dayjs";
import { useDispatch } from "react-redux";

import type { IconName } from "@similarweb/icons";
import { Box } from "@similarweb/ui-components/foundations";

import { PlainTooltip } from "app/components/React/Tooltip/PlainTooltip/PlainTooltip";
import { translate } from "app/filters/i18nFilter";
import { getEnrichedContactsCsvThunk } from "app/pages/sales-intelligence/modules/enrichment/store/effects/contactsEnrichment";
import { getEnrichedCsvThunk } from "app/pages/sales-intelligence/modules/enrichment/store/effects/enrichment";
import type { EnrichedState } from "app/pages/sales-intelligence/modules/enrichment/store/type";
import { EnrichmentStatus } from "app/pages/sales-intelligence/modules/enrichment/store/type";
import {
    DOWNLOAD_CSV_TOOLTIP,
    HISTORY_TOTAL_CONTACTS,
    TOTAL_ENRICHED,
    TOTAL_LEADS,
} from "app/pages/sales-intelligence/modules/enrichment/utils/i18n/modal.texts";
import { downloadFile } from "app/pages/sales-intelligence/modules/enrichment/utils/utils";
import LoaderGradient from "app/pages/sales-intelligence/pages/company-analysis/components/shared/Loader/LoaderGradient";
import enrichmentTrackingService from "app/pages/sales-intelligence/services/tracking/enrichment/trackingService";

import {
    StyledCaption,
    StyledCsvBlock,
    StyledCsvDate,
    StyledCsvMainData,
    StyledCsvMetaData,
    StyledCsvName,
    StyledCsvValue,
    StyledCustomIcon,
    StyledDownload,
    StyledEnrichedCsvWrapper,
    StyledIcon,
} from "./style";

type EnrichedItemProps = { enrichedState: EnrichedState } & { iconName?: IconName };

export const EnrichedItem = ({ enrichedState, iconName }: EnrichedItemProps) => {
    const dispatch = useDispatch();

    const { id, fileName, created, status } = enrichedState;
    const isLoading = status === EnrichmentStatus.InProgress;

    const { count, enrichedCount, translation } =
        enrichedState.enrichmentTypeName === "Contacts"
            ? {
                  count: enrichedState.contactsCount,
                  enrichedCount: enrichedState.enrichedContactsCount,
                  translation: HISTORY_TOTAL_CONTACTS,
              }
            : {
                  count: enrichedState.domainsCount,
                  enrichedCount: enrichedState.enrichedDomainsCount,
                  translation: TOTAL_LEADS,
              };

    const onSave = async () => {
        const link = await dispatch(
            enrichedState.enrichmentTypeName === "Contacts" ? getEnrichedContactsCsvThunk(id) : getEnrichedCsvThunk(id),
        );
        downloadFile(fileName, link);
        enrichmentTrackingService.csv.trackFileDownload(count, enrichedCount);
    };

    const getIcon = () => {
        if (iconName) {
            return (
                <StyledCustomIcon
                    iconName={iconName}
                    size="sm"
                />
            );
        }

        return (
            <StyledIcon
                iconName="list-icon"
                size="sm"
            />
        );
    };

    return (
        <StyledEnrichedCsvWrapper data-automation="download-csv-item">
            <StyledCsvBlock>
                {getIcon()}
                <StyledCsvMainData>
                    <StyledCsvName>{fileName}</StyledCsvName>
                    <StyledCsvDate>{dayjs.utc(created).format("DD.MM.YYYY")}</StyledCsvDate>
                </StyledCsvMainData>
            </StyledCsvBlock>

            <StyledCsvBlock>
                <StyledCsvMetaData>
                    {isLoading ? (
                        <LoaderGradient
                            height={40}
                            width={78}
                        />
                    ) : (
                        <>
                            <StyledCsvValue>{count}</StyledCsvValue>
                            <StyledCaption>{translate(translation)}</StyledCaption>
                        </>
                    )}
                </StyledCsvMetaData>

                <StyledCsvMetaData>
                    {isLoading ? (
                        <LoaderGradient
                            height={40}
                            width={118}
                        />
                    ) : (
                        <>
                            <StyledCsvValue>{enrichedCount}</StyledCsvValue>
                            <StyledCaption>{translate(TOTAL_ENRICHED)}</StyledCaption>
                        </>
                    )}
                </StyledCsvMetaData>

                <StyledCsvMetaData
                    className="noBorder"
                    width="80px"
                >
                    {isLoading ? (
                        <LoaderGradient
                            height={40}
                            width={40}
                        />
                    ) : (
                        <PlainTooltip text={translate(DOWNLOAD_CSV_TOOLTIP)}>
                            <Box onClick={onSave}>
                                <StyledDownload
                                    data-automation="download-csv-item-btn"
                                    className="download-csv"
                                    iconName="download"
                                    size="sm"
                                />
                            </Box>
                        </PlainTooltip>
                    )}
                </StyledCsvMetaData>
            </StyledCsvBlock>
        </StyledEnrichedCsvWrapper>
    );
};
